import React from "react";

/**
 * Reusable, column-configurable table for Insights Details sections.
 *
 * The proposal calls for a consistent tabular layout across sections whose
 * payloads differ (field extraction, score metrics, follow-up tasks, ...). So
 * this component is deliberately dumb/presentational: a caller builds `rows`
 * (plain objects) and a `columns` config, and every section shares one look.
 *
 *   columns: Array<{
 *     header: string,
 *     accessor: string | (row) => React.ReactNode,   // key or render fn
 *     cellStyle?: object,                             // per-column <td> style
 *     headerStyle?: object,                           // per-column <th> style
 *   }>
 *
 * Section-specific concerns (parsing, value mapping, N/A filtering, which
 * columns to show) live in the caller's adapter — see InsightPanel.
 *
 * Design notes honored via the CELL presets below:
 *   - All columns left-aligned (header AND data).
 *   - Primary field / metric: black, bold.
 *   - Evidence quote: grey, italic (secondary weight).
 *   - Confidence / score meta: grey.
 */

/** Semantic cell-style presets so every section styles columns consistently. */
export const CELL = {
  field: { fontWeight: 600, color: "#000" },
  meta: { color: "#555" },
  evidence: { color: "#666", fontStyle: "italic" },
};

/** Map a numeric confidence (5/3/1) to a High/Medium/Low label. */
export const confidenceLabel = (c) => {
  const n = Number(c);
  if (n === 5) return "High";
  if (n === 3) return "Medium";
  if (n === 1) return "Low";
  return c !== null && c !== undefined ? String(c) : "";
};

/** Humanize a snake_case / camelCase key into a Title Case label. */
export const humanizeKey = (key) => {
  if (typeof key !== "string") return "";
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase → spaced
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/** Pull a displayable evidence string out of a string or `{ evidence_text }`. */
export const evidenceToString = (ev) => {
  if (!ev) return "";
  if (typeof ev === "string") return ev;
  if (typeof ev === "object") {
    if (typeof ev.evidence_text === "string") return ev.evidence_text;
    try {
      return JSON.stringify(ev);
    } catch {
      return "";
    }
  }
  return String(ev);
};

/**
 * Flatten a nested "field extraction" JSON object into rows.
 * Handles scalars, arrays, nested groups, and `{ value, evidence, confidence }`
 * field objects (evidence as a string or `{ evidence_text }`).
 * @returns {Array<{field:string,value:string,confidence:*,evidence:string}>}
 */
export const buildInsightTableRows = (json, rows = []) => {
  if (!json || typeof json !== "object") return rows;

  Object.entries(json).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      const joined = value
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
        .join(", ");
      if (joined) rows.push({ field: humanizeKey(key), value: joined });
      return;
    }

    if (typeof value === "object") {
      const lowerKeys = Object.keys(value).map((k) => k.toLowerCase());
      const hasValue = lowerKeys.includes("value");
      const hasEvidence =
        lowerKeys.includes("evidence") || lowerKeys.includes("evidence_text");

      // A single field expressed as { value, evidence, confidence }.
      if (hasValue || hasEvidence) {
        rows.push({
          field: humanizeKey(key),
          value:
            value.value !== undefined && value.value !== null
              ? String(value.value)
              : "",
          evidence: evidenceToString(value.evidence || value.evidence_text),
          confidence: value.confidence,
        });
        return;
      }

      // Otherwise it's a nested group — recurse into its fields.
      buildInsightTableRows(value, rows);
      return;
    }

    rows.push({ field: humanizeKey(key), value: String(value) }); // scalar
  });

  return rows;
};

/**
 * Sibling-aware, single-level normalizer for a group object.
 *
 * Handles the `field` / `field_evidence` / `field_confidence_score` sibling
 * pattern (evidence & confidence are separate sibling keys, following the value
 * key in document order) as well as nested `{ value|score, evidence, confidence }`
 * objects. Used for the category-section groups (e.g. Lead Intake's
 * caller_context, conversion_momentum, ...).
 * @returns {Array<{field:string,value:string,confidence:*,evidence:string}>}
 */
export const groupRows = (obj, rows = []) => {
  if (!obj || typeof obj !== "object") return rows;
  let last = null;

  Object.entries(obj).forEach(([key, value]) => {
    const lk = key.toLowerCase();

    // Sibling attributes attach to the most recent primary row.
    if (last && /(?:_evidence|_evidence_text)$/.test(lk)) {
      if (!last.evidence) last.evidence = evidenceToString(value);
      return;
    }
    if (last && /(?:_confidence_score|_confidence)$/.test(lk)) {
      if (last.confidence === undefined || last.confidence === null)
        last.confidence = value;
      return;
    }
    if (last && /_details$/.test(lk)) {
      const d = value == null ? "" : String(value);
      const dn = d.trim().toLowerCase();
      if (d && !["na", "n/a", "not applicable", "not provided"].includes(dn))
        last.value = last.value ? `${last.value} — ${d}` : d;
      return;
    }
    if (/_segment$/.test(lk)) return; // call_segment is internal

    if (value === null || value === undefined) {
      last = null;
      return;
    }

    if (Array.isArray(value)) {
      const joined = value
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
        .join(", ");
      last = joined ? { field: humanizeKey(key), value: joined } : null;
      if (last) rows.push(last);
      return;
    }

    if (typeof value === "object") {
      const ok = Object.keys(value).map((k) => k.toLowerCase());
      const hasVal = ok.includes("value") || ok.includes("score");
      const hasEv = ok.includes("evidence") || ok.includes("evidence_text");
      if (hasVal || hasEv) {
        last = {
          field: humanizeKey(key),
          value: String(value.value ?? value.score ?? ""),
          evidence: evidenceToString(value.evidence || value.evidence_text),
          confidence: value.confidence ?? value.confidence_score,
        };
        rows.push(last);
      } else {
        groupRows(value, rows); // nested plain group
        last = null;
      }
      return;
    }

    const scalar =
      typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
    last = { field: humanizeKey(key), value: scalar };
    rows.push(last);
  });

  return rows;
};

// Inline styles keep the table self-contained (no dependency on the app's
// table CSS, which otherwise center-aligns cells) and reproduce faithfully in
// the screenshot-based PDF export. tableLayout:"fixed" + word wrapping keep
// long evidence text inside fixed-width columns instead of forcing a wide
// horizontal scrollbar.
// whiteSpace/overflow/textOverflow are set explicitly to defeat any inherited
// `white-space: nowrap; text-overflow: ellipsis` rule from the app's CSS that
// would otherwise clip long evidence/rationale text to a single truncated line.
// Two visual themes so the many section tables are clearly distinguishable
// from the two "scoring" tables at the top (MN Call Review & MH Evaluation
// Summary), per the design review:
//   - "scoring": teal header + grey zebra rows — matches .call-review-table.
//   - "section" (default): light-grey header with dark text + subtle
//     white/off-white zebra — a quieter look for the analytical sections.
export const TABLE_THEMES = {
  scoring: {
    headerBg: "#1f5d75",
    headerColor: "#fff",
    rowOdd: "#eef1f4",
    rowEven: "#e3e7eb",
    border: "#d0d7de",
    cellColor: "#333",
  },
  section: {
    headerBg: "#eceff1",
    headerColor: "#37474f",
    rowOdd: "#ffffff",
    rowEven: "#f4f6f8",
    border: "#e2e5e9",
    cellColor: "#2f3a41",
  },
};

// Padding/font kept to the bare minimum: the whole point of the tabular layout
// is to fit more on screen and make the page easy to scan, so cells are compact
// (tight padding, 13px, semibold field labels) rather than roomy.
const buildThStyle = (theme) => ({
  textAlign: "left",
  padding: "3px 8px",
  background: theme.headerBg,
  color: theme.headerColor,
  fontWeight: 600,
  border: `1px solid ${theme.border}`,
  fontSize: "13px",
  lineHeight: 1.3,
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "clip",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
});
const buildTdBase = (theme) => ({
  textAlign: "left",
  padding: "3px 8px",
  border: `1px solid ${theme.border}`,
  color: theme.cellColor,
  verticalAlign: "top",
  fontSize: "13px",
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "clip",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
});

// Defensive render guard: cell accessors normally return a string/number or a
// React element, but a not-fully-flattened insight field can be a raw object
// like { score, evidence }. Rendering that object directly throws "Objects are
// not valid as a React child" and takes down the whole panel/PDF, so coerce any
// stray object to a readable string instead of crashing.
const toSafeChild = (val) => {
  if (
    val == null ||
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean" ||
    React.isValidElement(val)
  ) {
    return val;
  }
  if (Array.isArray(val)) {
    return val.map((v, i) => (
      <React.Fragment key={i}>{toSafeChild(v)}</React.Fragment>
    ));
  }
  if (typeof val === "object") {
    if (val.value !== undefined || val.score !== undefined) {
      return String(val.value ?? val.score ?? "");
    }
    if (val.evidence !== undefined || val.evidence_text !== undefined) {
      return String(val.evidence ?? val.evidence_text ?? "");
    }
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
};

const InsightDataTable = ({ rows, columns, variant = "section" }) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (!Array.isArray(columns) || columns.length === 0) return null;

  const theme = TABLE_THEMES[variant] || TABLE_THEMES.section;
  const thStyle = buildThStyle(theme);
  const tdBase = buildTdBase(theme);
  const hasWidths = columns.some((c) => c.width);

  return (
    <div className="insight-data-table-wrapper">
      <table
        className="insight-data-table"
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}
      >
        {hasWidths && (
          <colgroup>
            {columns.map((c, i) => (
              <col key={i} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} style={{ ...thStyle, ...(c.headerStyle || {}) }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            // A row may opt into a distinct look (e.g. an average/total row) via
            // `__rowStyle`, which overrides the default zebra background.
            <tr
              key={ri}
              style={{
                backgroundColor: ri % 2 === 0 ? theme.rowOdd : theme.rowEven,
                ...(row.__rowStyle || {}),
              }}
            >
              {columns.map((c, ci) => {
                const content =
                  typeof c.accessor === "function"
                    ? c.accessor(row)
                    : row[c.accessor];
                return (
                  <td key={ci} style={{ ...tdBase, ...(c.cellStyle || {}) }}>
                    {toSafeChild(content)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InsightDataTable;
